/* eslint-disable id-length */
/* eslint-disable react/jsx-no-bind */
/**
 * A CSS-Grid based replacement for the <table> based TableView.
 *
 * Key changes compared to the original implementation:
 *   – Uses `display: grid` + CSS custom properties for column widths.
 *   – Column resize logic simply mutates those CSS variables – no layout compensation.
 *   – The “actions” column is defined as `1fr` so the browser naturally fills remaining space.
 *   – Still leverages react-virtual (@tanstack/react-virtual) for performant row virtualisation.
 *   – Preserves sorting, filtering and selection behaviour.
 */

import { useBuild } from "app/components/build/Show/lib/BuildContext";
import { Job } from "app/components/build/Show/lib/types";
import * as TableCell from "./TableCell";
import { TableActionsDropdown } from "./TableActionsDropdown";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { useBuildPreferencesStore } from "../../lib/useBuildPreferencesStore";
import Icon from "app/components/shared/Icon";
import { filterJobs, useFilterStore } from "../Sidebar/useFilterStore";
import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { resolveJobUrl } from "../../lib/urlForView";
import useQueryParams from "../../lib/useQueryParams";
import { useVirtualizer } from "@tanstack/react-virtual";

// ---------------------------------------------------------------------------
// Column config
// ---------------------------------------------------------------------------

interface Column {
  isFlexible?: boolean;
  key: string;
  label: string | React.ReactNode;
  component: (props: { job: Job }) => React.ReactNode;
  sortFn?: TableCell.SortFn;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  isResizable?: boolean;
}

const columns: ReadonlyArray<Column> = [
  {
    key: "state",
    label: "State",
    component: TableCell.State,
    sortFn: TableCell.sortByState,
    defaultWidth: 70,
    minWidth: 70,
    maxWidth: 200,
    isResizable: true,
  },
  {
    key: "label",
    label: "Label",
    component: TableCell.Label,
    sortFn: TableCell.sortByLabel,
    defaultWidth: 200,
    minWidth: 110,
    maxWidth: 800,
    isFlexible: true,
  },
  {
    key: "command",
    label: "Command",
    component: TableCell.Command,
    sortFn: TableCell.sortByCommand,
    defaultWidth: 120,
    minWidth: 90,
    maxWidth: 600,
    isFlexible: true,
  },
  {
    key: "queue",
    label: "Queue",
    component: TableCell.Queue,
    sortFn: TableCell.sortByQueue,
    defaultWidth: 100,
    minWidth: 80,
    maxWidth: 400,
    isFlexible: true,
  },
  {
    key: "agent",
    label: "Agent",
    component: TableCell.Agent,
    sortFn: TableCell.sortByAgent,
    defaultWidth: 130,
    minWidth: 80,
    maxWidth: 500,
    isFlexible: true,
  },
  {
    key: "wait_duration",
    label: "Wait time",
    component: TableCell.WaitDuration,
    sortFn: TableCell.sortByWaitDuration,
    defaultWidth: 120,
    minWidth: 80,
    maxWidth: 250,
  },
  {
    key: "run_duration",
    label: "Run time",
    component: TableCell.RunDuration,
    sortFn: TableCell.sortByRunDuration,
    defaultWidth: 120,
    minWidth: 80,
    maxWidth: 250,
  },
  {
    key: "start_time",
    label: "Started at",
    component: TableCell.StartTime,
    sortFn: TableCell.sortByStartTime,
    defaultWidth: 170,
    minWidth: 100,
    maxWidth: 800,
    isFlexible: true,
  },
  {
    key: "actions",
    label: <TableActionsDropdown />,
    // eslint-disable-next-line react/no-unused-prop-types
    component: () => (
      <div className="flex w-full justify-end">
        <span />
      </div>
    ),
    defaultWidth: 40,
    minWidth: 40,
    maxWidth: 40,
    isResizable: false,
  },
];

// ---------------------------------------------------------------------------
// Sorting helpers (copied from original)
// ---------------------------------------------------------------------------

const useTableSorting = () => {
  const sortDirection = useBuildPreferencesStore((state) => state.sortDirection);
  const setSortDirection = useBuildPreferencesStore((state) => state.setSortDirection);

  const sortColumn = useBuildPreferencesStore((state) => state.sortColumn);
  const setSortColumn = useBuildPreferencesStore((state) => state.setSortColumn);

  const clickCountRef = useRef(0);

  const setSort = (column: string) => {
    if (column === sortColumn) {
      clickCountRef.current += 1;
      if (clickCountRef.current > 1) {
        setSortColumn(null);
        setSortDirection(null);
        clickCountRef.current = 0;
      } else {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      }
    } else {
      clickCountRef.current = 0;
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  return { sortColumn, sortDirection, setSort };
};

// ---------------------------------------------------------------------------
// Column resize hook – writes directly to CSS variables for ultra-simple layout.
// ---------------------------------------------------------------------------

function useGridColumnResizing(columnsConfig: ReadonlyArray<Column>) {
  const setColumnWidth = useBuildPreferencesStore((state) => state.setColumnWidth);

  const [active, setActive] = useState<null | {
    key: string;
    startX: number;
    startWidth: number;
  }>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const handleMouseDown = useCallback(
    (key: string, e: React.MouseEvent<HTMLDivElement>) => {
      const colConfig = columnsConfig.find((c) => c.key === key);
      if (!colConfig) {
        return;
      }

      const headerEl = e.currentTarget.parentElement as HTMLElement | null;
      const actualWidth = headerEl
        ? headerEl.getBoundingClientRect().width
        : colConfig.defaultWidth;

      // Freeze current width so grid doesn't jump when switching to fixed mode
      document.documentElement.style.setProperty(`--col-${key}`, `${actualWidth}px`);

      setActive({ key, startX: e.clientX, startWidth: actualWidth });
      e.preventDefault();
      e.stopPropagation();
    },
    [columnsConfig],
  );

  useEffect(() => {
    if (!active) {
      return;
    }

    const colConfig = columnsConfig.find((c) => c.key === active.key)!;

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - active.startX;
      let newWidth = active.startWidth + dx;
      newWidth = Math.max(colConfig.minWidth, Math.min(colConfig.maxWidth, newWidth));
      document.documentElement.style.setProperty(`--col-${colConfig.key}`, `${newWidth}px`);
    };

    const onUp = () => {
      const finalWidth = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(`--col-${colConfig.key}`),
      );
      setColumnWidth(colConfig.key, finalWidth); // persist for future sessions
      setActive(null);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.userSelect = "none";
    document.documentElement.style.setProperty("cursor", "col-resize", "important");

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.documentElement.style.removeProperty("cursor");
    };
  }, [active, columnsConfig, setColumnWidth]);

  return {
    handleMouseDown,
    hoveredKey,
    setHoveredKey,
    resizingKey: active?.key ?? null,
  };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function TableView() {
  const { build } = useBuild();

  const { sortColumn, sortDirection, setSort } = useTableSorting();
  const filter = useFilterStore((state) => state.filterBy);
  const params = useQueryParams();

  // Initialise CSS vars once (or when persisted widths change)
  const columnWidths = useBuildPreferencesStore((state) => state.columnWidths);
  useEffect(() => {
    columns.forEach((col) => {
      const width = columnWidths[col.key] ?? col.defaultWidth;
      document.documentElement.style.setProperty(`--col-${col.key}`, `${width}px`);
    });
  }, [columnWidths]);

  // Resizing behaviour
  const { handleMouseDown, hoveredKey, setHoveredKey, resizingKey } =
    useGridColumnResizing(columns);

  // Build jobs list (filter + sort)
  const jobs = useMemo(() => {
    let list: Job[] =
      build?.jobs.filter(
        (j) => j.state !== "broken" && (j.type === "script" || j.type === "trigger"),
      ) || [];
    list = filterJobs(list, filter, build);
    const column = columns.find((c) => c.key === sortColumn);
    if (column?.sortFn && sortDirection) {
      list = [...list].sort(column.sortFn(sortDirection));
    }
    return list;
  }, [build, filter, sortColumn, sortDirection]);

  // Virtualisation setup
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: jobs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 38,
    overscan: 20,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();

  // Grid template columns string: every column except actions uses var(), actions uses 1fr
  const gridTemplateColumns = useMemo(() => {
    return columns
      .map((col) => {
        if (col.key === "actions") {
          return "minmax(var(--col-actions, 40px), 1fr)"; // flexible buffer
        }
        const userSet =
          columnWidths[col.key] !== undefined && columnWidths[col.key] !== col.defaultWidth;
        const isActive = resizingKey === col.key;
        if (userSet || isActive || !col.isFlexible) {
          // Keep fixed if user-sized, actively resizing, or not marked flexible
          return `var(--col-${col.key}, ${col.defaultWidth}px)`;
        }
        // Flexible column (label, command, etc.) adapts with viewport
        return `minmax(var(--col-${col.key}, ${col.defaultWidth}px), 1fr)`;
      })
      .join(" ");
  }, [columnWidths, resizingKey]);

  const totalMinWidth = useMemo(() => columns.reduce((sum, col) => sum + col.minWidth, 0), []);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const headerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex h-full w-full flex-col" data-testid="build-table">
      <div ref={parentRef} className="flex-auto overflow-auto">
        {/* Sticky Header inside scroll container so it scrolls horizontally together */}
        <div
          ref={headerRef}
          className="sticky top-0 z-10 border-b border-b-gray-400 bg-white backdrop-blur"
          style={{
            display: "grid",
            gridTemplateColumns,
            width: "fit-content",
            minWidth: "100%",
          }}
        >
          {columns.map((column, idx) => {
            const isLast = idx === columns.length - 1;
            const isHovered = hoveredKey === column.key;
            const isResizing = resizingKey === column.key;

            return (
              <div
                key={column.key}
                className={classNames("relative select-none px-2.5 py-2 first:pl-4 last:pr-1", {
                  "cursor-pointer hover:underline": column.sortFn,
                  underline: column.key === sortColumn,
                  "border-r border-gray-300": !isLast,
                })}
                style={{ minWidth: column.minWidth, maxWidth: column.maxWidth }}
              >
                {/* Header label */}
                <div
                  onClick={column.sortFn ? () => setSort(column.key) : undefined}
                  className="flex max-w-full items-center truncate font-medium"
                >
                  {typeof column.label === "string" ? (
                    <span className="max-w-full truncate">{column.label}</span>
                  ) : (
                    column.label
                  )}
                  {column.key === sortColumn && (
                    <Icon
                      icon={
                        sortDirection === "desc"
                          ? "heroicons/outline/arrow-down"
                          : "heroicons/outline/arrow-up"
                      }
                      className="ml-1 h-2 w-2 shrink-0"
                    />
                  )}
                </div>

                {/* Resize handle (if resizable) */}
                {column.isResizable !== false && (
                  <div
                    role="separator"
                    aria-orientation="vertical"
                    title="Drag to resize. Double-click to reset width."
                    onMouseDown={(e) => handleMouseDown(column.key, e)}
                    onDoubleClick={() => {
                      document.documentElement.style.setProperty(
                        `--col-${column.key}`,
                        `${column.defaultWidth}px`,
                      );
                      useBuildPreferencesStore
                        .getState()
                        .setColumnWidth(column.key, column.defaultWidth);
                    }}
                    onMouseEnter={() => setHoveredKey(column.key)}
                    onMouseLeave={() => setHoveredKey(null)}
                    className={classNames(
                      "absolute top-0 z-20 h-full w-4 cursor-col-resize touch-none select-none",
                      isLast ? "right-0" : "right-[-8px]",
                    )}
                  >
                    <div
                      style={{
                        // Render handle to the full height of the table (including header + 1px border)
                        height:
                          isHovered || isResizing
                            ? rowVirtualizer.getTotalSize() +
                              (headerRef.current?.clientHeight ?? 0) +
                              1
                            : "100%",
                      }}
                      className={classNames(
                        "pointer-events-none absolute left-[7px] top-0 h-full w-[2px]",
                        {
                          "bg-purple-600": isHovered || isResizing,
                          "bg-transparent": !isHovered && !isResizing,
                        },
                      )}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Rows */}
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: "relative",
            minWidth: totalMinWidth,
            width: "100%",
          }}
        >
          {virtualRows.map((virtualRow) => {
            const job = jobs[virtualRow.index];
            return (
              <Link
                key={job.id}
                to={resolveJobUrl(job.id)}
                id={`job-${job.id}`}
                data-testid="jobs-table-row"
                className={twMerge(
                  "absolute inset-x-0 grid select-text items-center px-0 py-0 after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gray-400 after:content-['']",
                  "text-charcoal-800 hover:bg-gray-100 hover:text-charcoal-900 hover:no-underline",
                  "focus:text-inherit focus:no-underline",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-opacity-50",
                  classNames({
                    "bg-gray-100": params.jobUuid === job.id || params.stepUuid === job.stepUuid,
                    "text-charcoal-300 hover:text-charcoal-300":
                      job.type === "script" && job.retriedInJobUuid,
                  }),
                )}
                style={{
                  top: 0,
                  transform: `translateY(${virtualRow.start}px)`,
                  height: `${virtualRow.size}px`,
                  gridTemplateColumns,
                  left: 0,
                  width: "fit-content",
                  minWidth: "100%",
                }}
              >
                {columns.map((column) => (
                  <div
                    key={column.key}
                    className="truncate px-2.5 py-2 first:pl-4 last:pr-1"
                    style={{ maxWidth: column.maxWidth, minWidth: column.minWidth }}
                  >
                    {column.component({ job }) || <span className="text-gray-800">—</span>}
                  </div>
                ))}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
